import axios from 'axios';
import {
	PageLayout,
	getThemeFromUrl,
	PostcodeLookup,
	StringHelpers,
	LangContext,
	route,
	API_ENDPOINTS,
	customToast,
	Variants,
	useForm,
	Button,
	Themes,
	SubTitle,
	WidthConstrainedContainer,
	Card,
	CardBody,
	BottomAnchoredControls,
	GasSafeLookup,
	useDisabledContext,
	useLangContext,
	Spinner,
	SelectableCardInput,
	ButtonTypes,
	Sizes,
	Icons,
	MultiFileInput,
	FormInput,
	SelectOption,
	useQuery,
	UploadDataType,
	FileHelpers,
	useBlockAnchorNavigation,
	checkTheme,
	useUserContext,
	CardData,
	DateFormats,
	formatDateFns,
	Roles,
	useAuthorisationContext,
} from 'carrier-fe';
import { useEffect, useMemo, useState, useContext } from 'react';
import { DocumentPayloadType, LangMyCompany } from '../../../types/lang/my-company';
import ViessmannGas from 'carrier-fe/src/images/viessmann_gas.jpg';
import ViessmannHeatpumps from 'carrier-fe/src/images/viessmann_heatpumps.jpg';
import ViessmannSolar from 'carrier-fe/src/images/viessmann_solar.jpg';
import ViessmannElectric from 'carrier-fe/src/images/viessmann_electric.svg';

const defaultData = {
	name: '',
	address_line_1: '',
	address_line_2: '',
	address_line_3: '',
	town_city: '',
	state_county: '',
	postcode_zipcode: '',
	country_code_iso_3: '',
	fgas_refcom_standard_number: '',
	account_number: '',
	dispatch_note_field_label: '',
	dispatch_note_required: '',
	distributor_claim_type: '',
	distributor_claim_inbox_email: '',
	gas_boiler_systems_enabled: false,
	heat_pump_systems_enabled: false,
	solar_systems_enabled: false,
	other_systems_enabled: false,
	gas_safe_number: '',
	gas_safe_confirmed: false,
	type: '',
	gas_safe_company_name: '',
	mcs_number: '',
	signature_name: '',
	signature_image: '',
	documents: [
		{
			document_type: '',
			expiry_date: '',
			files: [],
		} as DocumentPayloadType
	],
};

type Data = typeof defaultData;

type Distributor = { 
	name: string, 
	id: string 
};

type WarrantyCertificate = {
	distributor_id: string;
	distributor_name: string;
	file_name: string;
	download_url: string;
};

type Page<T> = {
	page: {
		fields: T;
		submit_button: {
			default: 'Update';
			submitting: 'Updating...';
		};
		title: 'My Company';
		sections: {
			details: 'Details';
			additional: 'Additional';
			installer: 'Installer';
			distributor: 'Distributor';
		};
		download_company_warranty_certificate: 'Download Company Warranty Certificate';
	};
};

function MyCompany() {
	const { getLangPage } = useContext(LangContext);
	const { fields, registration, crud } = useLangContext();
	const { companyDetailsConfirmedAt, refetchUser } = useUserContext();
	const { hasRoleAccess } = useAuthorisationContext();
	const isSuperAdminOrPlatformManager =
		hasRoleAccess(Roles.SUPER_ADMIN) ||
		hasRoleAccess(Roles.PLATFORM_MANAGER);
	const canManageSignature =
		isSuperAdminOrPlatformManager ||
		hasRoleAccess(Roles.DISTRIBUTION_MANAGER);
	const [ signatureFile, setSignatureFile ] = useState<any>(null);
	const [ lang, setLang ] = useState<Page<LangMyCompany>>();
	const theme = useMemo(() => getThemeFromUrl(), []);
	const [ claimType, setClaimType ] = useState([]);
	const [ distributors, setDistributors ] = useState<Distributor[]>([]);
	const [ warrantyCertificates, setWarrantyCertificates ] = useState<WarrantyCertificate[]>([]);
	const { disabled, setDisabled } = useDisabledContext();
	const [ gasSafeNameMismatch, setGasSafeNameMismatch ] = useState('');
	const [ needsToUpdateCompanyDetails, setNeedsToUpdateCompanyDetails ]
		=useState<boolean>(false);

	const registrationLang =
		registration?.page?.step_four[getThemeFromUrl()] || null;

	const { data: documentTypes } = useQuery<{ data: SelectOption[] }>(
		route(API_ENDPOINTS.GENERIC.SELECT.DOCUMENT_TYPES.COMPANY),
		{
			method: 'GET',
		}
	);

	const getUploadUrl = async () => {
		try {
			const response = await axios.get(
				route(API_ENDPOINTS.GENERIC.DOCUMENT.UPLOAD_URL)
			);

			return response?.data?.data;
		} catch (err: any) {
			throw new Error(
				err?.response?.data?.message
					|| err.message
					|| 'Could not start the file upload. Please try again.'
			);
		}
	};

	const read = async () => {
		if (disabled) return;
		setDisabled(true);

		try {
			const res = await axios.get<{ 
				data: Data & { 
					distributors: Distributor[];
					warranty_certificates?: WarrantyCertificate[];
				} 
			}>(route(API_ENDPOINTS.GENERIC.MY_COMPANY.EDIT));

			if (!res || !res.data) return;
			const { distributors, warranty_certificates, ...rest } = res.data.data;
			setValues(rest);
			setDistributors(distributors);
			setWarrantyCertificates(warranty_certificates ?? []);
		} catch (error: any) {
			if (!!error && !!error.response && !!error.response.data) {
				if (!!error.response.data.message) {
					customToast({
						title: error.response.data.message,
						variant: Variants.Danger,
					});
				}
			} else {
				console.log(error);
				customToast({
					title: 'An error occurred. Please refresh the page to try again.',
					variant: Variants.Danger,
				});
			}
		} finally {
			setDisabled(false);
		}
	};

	const update = async (payload: typeof defaultData) => {
		if (
			payload.gas_safe_number &&
			payload.gas_safe_company_name &&
			payload.name !== payload.gas_safe_company_name
		) {
			payload.name = payload.gas_safe_company_name;

			customToast({
				variant: Variants.Warning,
				title: 'Your company name has been updated to match the Gas Safe number details.',
			});

			setGasSafeNameMismatch(
				registrationLang?.gas_safe_desc ||
					'The details on the Gas Safe licence card that you provide will be checked against the registration details that you provide. If those details don’t match, your registration will need to be verified by a platform admin. Please make sure that the name you provide is identical to the name on your licence card.'
			);

			setValues({
				name: payload.gas_safe_company_name,
			});
		}
		
		const { dispatch_note_required, ...rest } = payload;

		const updatedPayload =
			dispatch_note_required === 'yes'
				? { ...rest, dispatch_note_required: true }
				: { ...rest, dispatch_note_required: false };
		
		if (signatureFile?.base64) {
			try {
				const uploadData: UploadDataType = await getUploadUrl();
				const response = await FileHelpers.upload(signatureFile, uploadData);

				if (!response?.ok) {
					throw new Error(
						'Your signature image could not be uploaded. Please try again.'
					);
				}

				(updatedPayload as any).signature_image = uploadData.key;
			} catch (error: any) {
				return Promise.reject({
					response: {
						data: {
							message: error.message || 'Signature upload failed'
						}
					}
				});
			}
		}

		const filesToUpload = updatedPayload.documents
            ?.filter(doc => doc?.files?.length > 0)
            ?.flatMap(doc => doc?.files) ?? [];

        if (filesToUpload.length > 0) {
            try {
                await Promise.all(
                    filesToUpload.map(async (file) => {
                        if (!file.base64) return;
                        const uploadData: UploadDataType = await getUploadUrl();
                        const response = await FileHelpers.upload(file, uploadData);
                        
                        if (!response?.ok) {
                            throw new Error(
                                `"${file.name}" could not be uploaded. Please try again.`
                            );
                        }

                        file.aws_url = uploadData.key;
                        delete file.base64;
                    })
                );
            } catch (error: any) {
                const shapedError = {
                    response: {
                        data: {
                            message: error.message || 'Upload failed'
                        }
                    }
                };
                return Promise.reject(shapedError);
            }
        };

		return axios.patch<{ data: Data; message: string }>(
			route(API_ENDPOINTS.GENERIC.MY_COMPANY.UPDATE),
			updatedPayload
		);
	};

	const { 
		data: patchResponse,
		Input, 
		Submit, 
		errors, 
		store, 
		setValues,  
		isSuccess
	} = useForm(
		update,
		defaultData
	);

	useBlockAnchorNavigation(
		!isSuccess && needsToUpdateCompanyDetails, 
		() => customToast({
			title: crud?.alerts?.toshiba?.company_details
				|| 'Please make sure your company details are correct and up to date.',
			variant: Variants.Danger
		})
	);

	const handleGasSafeLookupChange = (data: any) => {
		const gasSafeCompanyName = data?.company?.name;

		const nameMismatch =
			gasSafeCompanyName && store.name !== gasSafeCompanyName;

		if (nameMismatch) {
			setValues({
				name: gasSafeCompanyName,
				gas_safe_company_name: gasSafeCompanyName,
				gas_safe_confirmed: false,
			});

			customToast({
				variant: Variants.Warning,
				title: 'Your company name has been updated to match the Gas Safe number details.',
			});

			setGasSafeNameMismatch(
				registrationLang?.gas_safe_desc ||
					'The details on the Gas Safe licence card that you provide will be checked against the registration details that you provide. If those details don’t match, your registration will need to be verified by a platform admin. Please make sure that the name you provide is identical to the name on your licence card.'
			);
		} else {
			setGasSafeNameMismatch('');
		}
	};
	
	const handleChange = (index: number, field: string, value: unknown) => {
        const updatedDocuments = [...store?.documents];
        updatedDocuments[index][field] = value;
        setValues({ documents: updatedDocuments });
    };

	// Warranty certificate endpoints are auth-gated so a plain <a href> won't
	// carry the bearer token. Fetch the PDF as a blob via axios (which uses
	// the auth interceptor) and trigger a download from a synthetic link.
	const downloadWarrantyCertificate = async (cert: WarrantyCertificate) => {
		setDisabled(true);
		try {
			const response = await axios.get(cert.download_url, {
				responseType: 'blob',
			});
			const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = objectUrl;
			link.setAttribute('download', `${cert.file_name}.pdf`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(objectUrl);
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message
					|| 'Failed to download warranty certificate',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const addDocument = () => {
        setValues({ documents: [
            ...(store?.documents ?? []), 
			{ 
                document_type: '', 
                expiry_date: '',
                files: [],
				id: '' 
            }
        ] });
    };

    const removeDocument = (index: number) => {
        const updatedDocuments = store?.documents.filter(
			(_, i) => i !== index
		);
        setValues({ documents: updatedDocuments });
    };

	const hasSystemError = useMemo(() => {
		for (let k of [
			'gas_boiler_systems_enabled',
			'heat_pump_systems_enabled',
			'solar_systems_enabled',
			'other_systems_enabled',
		]) {
			if (errors && errors[k]) return true;
		}

		return false;
	}, [errors]);

	const viewable = (props: {
		themes?: typeof theme | (typeof theme)[];
		type?: 'distributor' | 'installer';
	}) => {
		const { themes, type } = props;

		if (themes && ![themes].flat().includes(theme)) {
			return false;
		}

		if (type && store.type !== type) {
			return false;
		}

		return true;
	};

	const toshibaInstaller = viewable({
		themes: Themes.Toshiba,
		type: 'installer',
	});

	const toshibaDistributor = viewable({
		themes: Themes.Toshiba,
		type: 'distributor',
	});

	const vokeraViessmann = viewable({
		themes: [Themes.Vokera, Themes.Viessmann],
	});

	useEffect(() => {
		read();
		getLangPage('dashboard/my_company').then((res: Page<LangMyCompany>) =>
			setLang(res)
		);
	}, []);

	useEffect(() => {
		if (toshibaDistributor) {
			axios
				.get(
					route(API_ENDPOINTS.GENERIC.SELECT.DISTRIBUTOR_CLAIM_TYPES)
				)
				.then((res) => setClaimType(res.data.data))
				.catch((err) => {
					console.error(err);
				});
		}
	}, [toshibaDistributor]);

	useEffect(() => {
		refetchUser?.();
		setValues({
			documents: patchResponse?.documents
		});
	}, [patchResponse]);

	return (
		<PageLayout
			title={StringHelpers.title(lang?.page.title || 'My Company')}
			setNeedsToUpdateCompanyDetails={setNeedsToUpdateCompanyDetails}
		>
			<WidthConstrainedContainer>
				{disabled ? (
					<div className="d-flex justify-content-center align-items-center vh-100">
						<Spinner />
					</div>
				) : (
					<>
						<Card>
							<CardBody>
								<SubTitle
									title={StringHelpers.title(
										lang?.page.sections.details || 'Details'
									)}
									className="mb-3 w-100"
									style={{ color: '#464C5E' }}
								/>
								{Input({
									name: 'name',
									label: lang?.page.fields.name || 'Name',
									disable: checkTheme(Themes.Toshiba) && !isSuperAdminOrPlatformManager,
								})}
								{checkTheme(Themes.Toshiba) &&
									<div className='row'>
										<CardData.Column>
											<CardData.ColumnItem
												title={StringHelpers.title(
													fields?.type
													|| 'type'
												)}
												value={StringHelpers.title(
													store?.type
												)}
											/>
										</CardData.Column>
										{distributors?.length > 0 &&
											<CardData.Column>
												<CardData.ColumnItem
													title={StringHelpers.title(
														fields?.distributor_ids 
														|| 'Distributors'
													)}
													value={
														<ul className='list-unstyled'>
															{distributors?.map(distributor => 
																<li key={distributor.id}>
																	{distributor.name}
																</li>
															)}
														</ul>
													}
												/>
											</CardData.Column>
										}
										<CardData.Column>
											<CardData.ColumnItem
												title={StringHelpers.title(
													fields?.confirmed_at
													|| 'confirmed at'
												)}
												value={formatDateFns(
													companyDetailsConfirmedAt || '-',
													DateFormats.DATE
												)}
											/>
										</CardData.Column>	
									</div>
								}
							</CardBody>
						</Card>
						<Card>
							<CardBody>
								<PostcodeLookup
									data={store}
									setData={setValues}
									errors={errors}
								/>
							</CardBody>
						</Card>
						{toshibaInstaller && (
							<Card>
								<CardBody>
									<SubTitle
										title={StringHelpers.title(
											lang?.page.sections.installer ||
												'Installer'
										)}
										className="mb-3 w-100"
										style={{ color: '#464C5E' }}
									/>
									{Input({
										name: 'fgas_refcom_standard_number',
										label:
											lang?.page.fields
												.fgas_refcom_standard_number ||
											'FGAS / REFCOM standard number',
										visible: toshibaInstaller,
									})}
									{Input({
										name: 'account_number',
										label:
											lang?.page.fields.account_number ||
											'Account number',
										visible: toshibaInstaller,
									})}
								</CardBody>
							</Card>
						)}
						{toshibaDistributor && (
							<Card>
								<CardBody>
									<SubTitle
										title={StringHelpers.title(
											lang?.page.sections.distributor ||
												'Distributor'
										)}
										className="mb-3 w-100"
										style={{ color: '#464C5E' }}
									/>
									{Input({
										name: 'dispatch_note_field_label',
										label:
											lang?.page.fields
												.dispatch_note_field_label ||
											'Dispatch note field label',
										type: 'textarea',
										visible: toshibaDistributor,
									})}
									{Input({
										name: 'dispatch_note_required',
										label:
											lang?.page.fields
												.dispatch_note_required ||
											'Dispatch note required',
										type: 'select',
										options: [
											{
												label: 'Yes',
												value: 'yes',
											},
											{
												label: 'No',
												value: 'no',
											},
										],
										visible: toshibaDistributor,
									})}
									{Input({
										type: 'select',
										name: 'distributor_claim_type',
										label:
											lang?.page.fields
												.distributor_claim_type ||
											'Distributor claim type',
										visible: toshibaDistributor,
										options: claimType,
									})}
									{Input({
										name: 'distributor_claim_inbox_email',
										label:
											lang?.page.fields
												.distributor_claim_inbox_email ||
											'Distributor claims inbox email',
										visible: toshibaDistributor,
									})}
									{canManageSignature && (
										<>
											{Input({
												name: 'signature_name',
												label:
													(lang?.page.fields as any)
														?.signature_name ||
													fields?.signature_name ||
													'Signature name',
												visible: toshibaDistributor,
											})}
											<MultiFileInput
												value={signatureFile ? [signatureFile] : []}
												maxFiles={1}
												onChange={(value) =>
													setSignatureFile(value?.[0] ?? null)
												}
												label={StringHelpers.title(
													fields?.signature_image ||
														'Signature image'
												)}
												onReject={(value) => {
													customToast({
														title:
															value ||
															"Error with the file you've selected",
														variant: Variants.Danger,
													});
												}}
												errorField={Object.hasOwn(
													errors || {},
													'signature_image'
												)}
											/>
											{(store as any)?.signature_image &&
												!signatureFile && (
													<img
														src={(store as any).signature_image}
														alt="Signature"
														style={{
															maxHeight: 100,
															marginBottom: 16,
														}}
													/>
												)}
										</>
									)}
								</CardBody>
							</Card>
						)}
						{vokeraViessmann && (
							<Card>
								<CardBody>
									<SubTitle
										title={StringHelpers.title(
											lang?.page.sections.installer ||
												'Installer'
										)}
										className="mb-3 w-100"
										style={{ color: '#464C5E' }}
									/>
									<div className="row row-cols-1 row-cols-md-2 row-cols-lg-4">
										<div className="col">
											<SelectableCardInput
												value={
													store?.gas_boiler_systems_enabled
												}
												imageSrc={ViessmannGas}
												label={StringHelpers.title(
													fields?.company_gas_boiler_systems_enabled
												)}
												errorField={hasSystemError}
												isSelected={
													store?.gas_boiler_systems_enabled
												}
												onChange={(value: any) => {
													setValues({
														gas_boiler_systems_enabled:
															!value,
													});
												}}
											/>
										</div>
										<div className="col">
											<SelectableCardInput
												value={
													store?.heat_pump_systems_enabled
												}
												imageSrc={ViessmannHeatpumps}
												label={StringHelpers.title(
													fields?.company_heat_pump_systems_enabled
												)}
												errorField={hasSystemError}
												isSelected={
													store?.heat_pump_systems_enabled
												}
												onChange={(value: any) => {
													setValues({
														heat_pump_systems_enabled:
															!value,
													});
												}}
											/>
										</div>
										<div className="col">
											<SelectableCardInput
												value={
													store?.solar_systems_enabled
												}
												imageSrc={ViessmannSolar}
												label={StringHelpers.title(
													fields?.company_solar_systems_enabled
												)}
												isSelected={
													store?.solar_systems_enabled
												}
												errorField={hasSystemError}
												onChange={(value: any) => {
													setValues({
														solar_systems_enabled:
															!value,
													});
												}}
											/>
										</div>
										<div className="col">
											<SelectableCardInput
												value={
													store?.other_systems_enabled
												}
												imageSrc={ViessmannElectric}
												label={StringHelpers.title(
													fields?.company_other_systems_enabled
												)}
												errorField={hasSystemError}
												isSelected={
													store?.other_systems_enabled
												}
												onChange={(value: any) => {
													setValues({
														other_systems_enabled:
															!value,
													});
												}}
											/>
										</div>
									</div>
									<div>
										{Input({
											name: 'mcs_number',
											label: lang?.page.fields.mcs_number || 'Mcs Number',
										})}
									</div>
									<GasSafeLookup
										type="company"
										fetchOnLoad
										disabled={disabled}
										displayCard
										gasSafeNumber={
											store?.gas_safe_number || ''
										}
										gasSafeConfirmed={
											store?.gas_safe_confirmed
										}
										onGasSafeNumberChange={(value) =>
											setValues({
												gas_safe_number: value,
											})
										}
										onGasSafeConfirmedChange={(value) =>
											setValues({
												gas_safe_confirmed: value,
											})
										}
										errors={{
											gasSafeNumber:
												errors?.gas_safe_number,
											gasSafeConfirmed:
												errors?.gas_safe_confirmed,
										}}
										labels={{
											gasSafeNumber:
												fields?.gas_safe_number,
											gasSafeConfirmed:
												fields?.gas_safe_confirmation_company,
										}}
										warningMessage={gasSafeNameMismatch}
										onGasSafeLookupChange={
											handleGasSafeLookupChange
										}
									/>
								</CardBody>
							</Card>
						)}
						{toshibaInstaller && (
							<Card>
								<CardBody>
									<SubTitle
										style={{color: '#464C5E'}}
										title={crud?.sub_titles?.documents || 'Documents'}
										className='mb-3'
									/>
									{store?.documents?.map((document, index) => {
										return (
											<Card key={index}>
												<CardBody>
													{!document?.id && (
														<div className="d-flex justify-content-end mb-4">
															<Button
																label={StringHelpers.title(
																	crud?.buttons?.delete?.default ||
																		'Delete'
																)}
																onClick={() => removeDocument(index)}
																disabled={disabled}
																type={ButtonTypes.Outline}
																variant={Variants.Danger}
																size={Sizes.Small}
																rightIcon={Icons.DELETE}
															/>
														</div>
													)}
													<MultiFileInput
														value={document.files}
														maxFiles={3}
														onChange={(value) =>
															setValues({
																documents: store?.documents?.map((doc, idx) =>
																	idx === index
																		? { ...doc, files: value ?? [] }
																		: { ...doc, files: doc.files ?? [] }
																),
															})
														}
														label={StringHelpers.title(fields?.files || 'files')}
														onReject={(value) => {
															customToast({
																title: value || "Error with the files you've selected",
																variant: Variants.Danger
															});
														}}
														disabled={!!document?.id}
														errorField={
															Object.hasOwn(errors || {}, `documents.${index}.document_type`) ||
															Object.hasOwn(errors || {}, `documents.${index}.expiry_date`)
														}
													/>
													<div className='row'>
														<div className='col-12 col-lg-6'>
															<FormInput
																type='select'
																label={StringHelpers.title(
																	fields?.type || 'Type'
																)}
																placeholder={StringHelpers.title(
																	fields?.select || 'Select'
																)}
																name='document_type'
																value={document.document_type}
																options={documentTypes?.data?.map((docType) => {
																	const alreadySelected = store?.documents.some((doc) => 
																		doc.document_type === docType.value)

																	return ({
																		label: docType.label,
																		value: docType.value,
																		disabled: alreadySelected
																	})
																})}
																disabled={disabled}
																onChange={(value) => {
																	handleChange(index, 'document_type', value);
																}}
																errorMessages={
																	!document.document_type
																	? errors?.[`documents.${index}.expiry_date`] 
																		|| errors?.[`documents.${index}.files.0.name`]
																	: undefined
																}
															/>
														</div>
														<div className='col-12 col-lg-6'>
															<FormInput
																type='date'
																name='expiry_date'
																label={fields?.expiry_date || 'Expiry Date'}
																value={document.expiry_date}
																onChange={(value) => {
																	if (value === '__/__/____') value = ''
																	handleChange(index, 'expiry_date', value);
																}}
																disabled={disabled}
																errorMessages={
																	!document.expiry_date
																	? errors?.[`documents.${index}.document_type`] 
																		|| errors?.[`documents.${index}.files.0.name`]
																	: undefined
																}           
															/>
														</div>
													</div>
												</CardBody>
											</Card>
										)
									})}
									<div className="d-flex justify-content-end">
										<Button
											label={
												`${crud?.buttons?.add?.default 
													|| 'Add'
												} ${crud?.models?.document 
													|| 'Document'}`
											}
											type={ButtonTypes.Outline}
											disabled={disabled}
											variant={Variants.Dark}
											size={Sizes.Small}
											onClick={addDocument}
										/>
									</div>
								</CardBody>
							</Card>
						)}
						{toshibaInstaller && warrantyCertificates.length > 0 && (
							<Card>
								<CardBody>
									<SubTitle
										style={{ color: '#464C5E' }}
										title={StringHelpers.title(
											fields?.warranty_certificates
											|| 'Warranty Certificates'
										)}
										className='mb-3'
									/>
									{warrantyCertificates.map((cert) => (
										<div
											key={cert.distributor_id}
											className='d-flex justify-content-between align-items-center mb-2'
										>
											<div>{cert.file_name}</div>
											<Button
												label={StringHelpers.title(
													crud?.select_labels?.download
													|| 'Download'
												)}
												type={ButtonTypes.Outline}
												variant={Variants.Dark}
												size={Sizes.Small}
												disabled={disabled}
												onClick={() => downloadWarrantyCertificate(cert)}
											/>
										</div>
									))}
								</CardBody>
							</Card>
						)}
					</>
				)}
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Submit
					defaultLabel={lang?.page.submit_button.default || 'Upload'}
					loadingLabel={
						lang?.page.submit_button.submitting || 'Uploading'
					}
				/>
			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default MyCompany;
